/**
 * iter845f — POS Failures inspector.
 *
 * Route: /admin/pos-failures  (admin + manager).
 *
 * Table of the last N `checkout-fail` events recorded via
 * /api/pos/telemetry. Filters: branch, cashier, resolved / unresolved.
 * Click a row → expand a chronological breadcrumb timeline. Toggle
 * "Mark resolved" writes back via PATCH /api/pos/telemetry/{id}/resolved
 * so ops can turn the raw stream into an incident-triage workflow.
 */
import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronRight, CheckCircle2, RotateCcw, Copy, Filter, User, Building2, Clock, LayoutList, LayoutGrid, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  } catch { return iso; }
};

const fmtHms = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().split('T')[1].replace('Z', '');
  } catch { return iso; }
};

// iter845g — inline SVG sparkline. Renders `points` as a soft-filled
// area chart with a highlighted last day. Zero external deps.
const Sparkline = ({ points, width = 120, height = 32, tone = 'rose' }) => {
  if (!points || points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count || 0));
  const stepX = width / Math.max(1, points.length - 1);
  const yFor = (c) => height - (c / max) * (height - 4) - 2;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${yFor(p.count || 0).toFixed(1)}`).join(' ');
  const areaPath = `${path} L${((points.length - 1) * stepX).toFixed(1)},${height} L0,${height} Z`;
  const last = points[points.length - 1];
  const lastX = (points.length - 1) * stepX;
  const lastY = yFor(last.count || 0);
  const colours = {
    rose: { stroke: '#e11d48', fill: 'rgba(225,29,72,0.15)', dot: '#e11d48' },
    slate: { stroke: '#64748b', fill: 'rgba(100,116,139,0.15)', dot: '#64748b' },
  }[tone] || { stroke: '#e11d48', fill: 'rgba(225,29,72,0.15)', dot: '#e11d48' };
  return (
    <svg width={width} height={height} className="shrink-0" aria-label="7-day trend">
      <path d={areaPath} fill={colours.fill} />
      <path d={path} fill="none" stroke={colours.stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={2.5} fill={colours.dot} />
    </svg>
  );
};

const GroupCard = ({ group, onDrilldown }) => {
  const branches = group.branches?.length ? group.branches.join(', ') : '—';
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`group-card-${group.signature.slice(0, 20)}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className="bg-rose-600 text-white text-[9px]">{group.count} events</Badge>
              {group.open_count > 0 && <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-700">{group.open_count} open</Badge>}
              {group.resolved_count > 0 && <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">{group.resolved_count} resolved</Badge>}
            </div>
            <div className="text-xs font-mono text-foreground break-all leading-tight" title={group.signature}>
              {group.signature.length > 140 ? group.signature.slice(0, 140) + '…' : group.signature}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><Building2 size={10} /> {branches}</span>
              <span className="inline-flex items-center gap-1"><Clock size={10} /> last {fmtWhen(group.last_seen)}</span>
              <span>first {fmtWhen(group.first_seen)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <div className="flex items-center gap-1 text-[9px] font-semibold text-muted-foreground">
              <TrendingUp size={10} /> 7-day trend
            </div>
            <Sparkline points={group.sparkline} tone={group.open_count > 0 ? 'rose' : 'slate'} />
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] mt-1"
              onClick={() => onDrilldown(group)}
              data-testid={`group-drilldown-${group.signature.slice(0, 20)}`}
            >
              View events
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CrumbRow = ({ crumb }) => {  const stepTone = crumb.step?.includes('fail')
    ? 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
    : crumb.step?.includes('ok') || crumb.step === 'order-created'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
      : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
  return (
    <div className="flex items-start gap-2 py-1.5 border-l-2 border-border pl-3 relative">
      <span className="absolute -left-1 top-2 w-2 h-2 rounded-full bg-primary" />
      <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0 tabular-nums">{fmtHms(crumb.iso)}</span>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${stepTone} shrink-0`}>{crumb.step}</span>
      {crumb.ctx && (
        <span className="text-[10px] font-mono text-muted-foreground break-all">
          {JSON.stringify(crumb.ctx)}
        </span>
      )}
    </div>
  );
};

const FailureRow = ({ event, expanded, onToggle, onResolveToggle }) => {
  const isResolved = !!event.resolved;
  return (
    <div className={`rounded-lg border ${isResolved ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-rose-200 bg-rose-50/40 dark:bg-rose-950/10'}`} data-testid={`failure-row-${event.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/50 transition-colors rounded-lg"
        data-testid={`failure-toggle-${event.id}`}
      >
        <div className="mt-0.5 shrink-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isResolved ? (
              <Badge className="bg-emerald-600 text-white text-[9px]">Resolved</Badge>
            ) : (
              <Badge className="bg-rose-600 text-white text-[9px]">Open</Badge>
            )}
            <span className="text-xs font-semibold text-foreground truncate">{event.summary || event.event || 'checkout-fail'}</span>
          </div>
          {event.error && (
            <div className="text-[11px] text-muted-foreground mt-1 truncate font-mono">{event.error}</div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock size={10} /> {fmtWhen(event.created_at)}</span>
            {event.cashier_name && <span className="inline-flex items-center gap-1"><User size={10} /> {event.cashier_name}</span>}
            {event.branch_id && <span className="inline-flex items-center gap-1"><Building2 size={10} /> {event.branch_id}</span>}
            <span className="inline-flex items-center gap-1">{(event.breadcrumbs || []).length} crumbs</span>
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onResolveToggle(event); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onResolveToggle(event); } }}
          className={`shrink-0 h-7 px-2 rounded-md text-[10px] font-semibold inline-flex items-center gap-1 border transition-colors cursor-pointer ${isResolved ? 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/40'}`}
          data-testid={`failure-resolve-${event.id}`}
        >
          {isResolved ? <><RotateCcw size={11} /> Reopen</> : <><CheckCircle2 size={11} /> Resolve</>}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-1" data-testid={`failure-crumbs-${event.id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Breadcrumb timeline</div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-[10px]"
              onClick={async () => {
                const txt = (event.breadcrumbs || [])
                  .map((c) => `${fmtHms(c.iso)}  ${c.step}${c.ctx ? ' ' + JSON.stringify(c.ctx) : ''}`)
                  .join('\n');
                try {
                  await navigator.clipboard.writeText(txt);
                  toast.success('Breadcrumbs copied');
                } catch {
                  window.prompt('Copy the breadcrumbs:', txt);
                }
              }}
              data-testid={`failure-copy-${event.id}`}
            >
              <Copy size={10} className="mr-1" /> Copy
            </Button>
          </div>
          {(event.breadcrumbs || []).length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No breadcrumbs recorded.</div>
          ) : (
            <div className="ml-1">
              {(event.breadcrumbs || []).map((c, i) => <CrumbRow key={i} crumb={c} />)}
            </div>
          )}
          {event.resolved && event.resolved_note && (
            <div className="mt-3 p-2 rounded-md bg-emerald-100/50 dark:bg-emerald-950/30 border border-emerald-200 text-[11px] text-emerald-800 dark:text-emerald-300">
              <span className="font-semibold">Resolution note by {event.resolved_by || 'ops'}:</span> {event.resolved_note}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function PosFailuresPage() {
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // iter845g — 'list' shows individual events; 'grouped' rolls-up by
  // error signature over the last 7 days.
  const [viewMode, setViewMode] = useState('list');

  // Filters
  const [branchFilter, setBranchFilter] = useState('');
  const [cashierFilter, setCashierFilter] = useState('');
  const [resolvedFilter, setResolvedFilter] = useState('all'); // all | open | resolved

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (branchFilter.trim()) params.branch_id = branchFilter.trim();
      if (cashierFilter.trim()) params.cashier_id = cashierFilter.trim();
      if (resolvedFilter === 'open') params.resolved = false;
      if (resolvedFilter === 'resolved') params.resolved = true;
      const { data } = await api.get('/pos/telemetry/recent', { params });
      setEvents(data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load POS failures');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const params = { days: 7, limit: 20 };
      if (branchFilter.trim()) params.branch_id = branchFilter.trim();
      const { data } = await api.get('/pos/telemetry/grouped', { params });
      setGroups(data?.groups || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load grouped failures');
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    if (viewMode === 'grouped') fetchGroups();
    else fetchEvents();
  };

  useEffect(() => { refresh(); }, [viewMode]);

  const stats = useMemo(() => {
    const open = events.filter(e => !e.resolved).length;
    return { total: events.length, open, resolved: events.length - open };
  }, [events]);

  const handleResolveToggle = async (event) => {
    const next = !event.resolved;
    const note = next ? (window.prompt('Add a short resolution note (optional):', '') || '') : '';
    try {
      const { data } = await api.patch(`/pos/telemetry/${event.id}/resolved`, {
        resolved: next,
        note,
      });
      setEvents(prev => prev.map(e => e.id === event.id ? { ...e, ...data, resolved: next } : e));
      toast.success(next ? 'Marked as resolved' : 'Reopened');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="pos-failures-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center">
              <AlertTriangle size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">POS Failures</h1>
              <p className="text-xs text-muted-foreground">Checkout-fail telemetry captured across all branches. Expand to inspect the exact step sequence.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* iter845g — View toggle */}
            <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40" data-testid="view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`h-7 px-2 text-[10px] font-semibold rounded-md inline-flex items-center gap-1 transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-stone-800 shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-mode-list"
              >
                <LayoutList size={11} /> List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grouped')}
                className={`h-7 px-2 text-[10px] font-semibold rounded-md inline-flex items-center gap-1 transition-colors ${viewMode === 'grouped' ? 'bg-white dark:bg-stone-800 shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-mode-grouped"
              >
                <LayoutGrid size={11} /> Grouped (7d)
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="refresh-failures-btn">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total (shown)</div>
            <div className="text-2xl font-bold tabular-nums mt-1" data-testid="stat-total">{stats.total}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Open</div>
            <div className="text-2xl font-bold text-rose-600 dark:text-rose-300 tabular-nums mt-1" data-testid="stat-open">{stats.open}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Resolved</div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-300 tabular-nums mt-1" data-testid="stat-resolved">{stats.resolved}</div>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <Card><CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-muted-foreground">
            <Filter size={12} /> Filters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px]">Branch id</Label>
              <Input value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} placeholder="any branch" className="h-8 mt-0.5" data-testid="filter-branch" />
            </div>
            <div>
              <Label className="text-[10px]">Cashier id</Label>
              <Input value={cashierFilter} onChange={(e) => setCashierFilter(e.target.value)} placeholder={viewMode === 'grouped' ? 'not applied in grouped view' : 'any cashier'} className="h-8 mt-0.5" disabled={viewMode === 'grouped'} data-testid="filter-cashier" />
            </div>
            <div>
              <Label className="text-[10px]">Status</Label>
              <Select value={resolvedFilter} onValueChange={setResolvedFilter} disabled={viewMode === 'grouped'}>
                <SelectTrigger className="h-8 mt-0.5" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open only</SelectItem>
                  <SelectItem value="resolved">Resolved only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={refresh} disabled={loading} className="h-8 w-full" data-testid="apply-filters-btn">Apply</Button>
            </div>
          </div>
        </CardContent></Card>

        {/* List — either individual events or grouped signatures */}
        <div className="space-y-2" data-testid="failures-list">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Loading failures…</div>
          ) : viewMode === 'grouped' ? (
            groups.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No POS failures in the last 7 days. Nice &mdash; that&apos;s the goal.
              </div>
            ) : (
              groups.map((g) => (
                <GroupCard
                  key={g.signature}
                  group={g}
                  onDrilldown={() => {
                    // Drill into list view filtered by this signature's most-recent branch.
                    setViewMode('list');
                    setBranchFilter(g.branches?.[0] || '');
                    setResolvedFilter('all');
                    // Fetch happens via useEffect on viewMode change.
                  }}
                />
              ))
            )
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No POS failures recorded {resolvedFilter !== 'all' ? `for status "${resolvedFilter}"` : ''}. Nice &mdash; that&apos;s the goal.
            </div>
          ) : (
            events.map(ev => (
              <FailureRow
                key={ev.id}
                event={ev}
                expanded={expandedId === ev.id}
                onToggle={() => setExpandedId(expandedId === ev.id ? null : ev.id)}
                onResolveToggle={handleResolveToggle}
              />
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
